import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Avatar,
  Stack,
  FormControl,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Client } from "api/models/Client";
import { Snackbar } from "types/snackbar";
import { useDropzone } from "react-dropzone";
import { green, grey, indigo, red } from "@mui/material/colors";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import { uploadFRRBids } from "api/resources/clients";
import { fetchClientFRRBids } from "store/appSlice";
import { selectFRRBids } from "store/selectors";
import { useAppDispatch, useAppSelector } from "hooks";
import Table from "components/Table";
import dayjs from "dayjs";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Select from "components/Select";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";

interface FRRBidsProps {
  client: Client;
  setSnackbar: React.Dispatch<React.SetStateAction<Snackbar>>;
}

interface FormValues {
  uploadedAtFrom: string | null;
  uploadedAtTo: string | null;
  processType: string | null;
  reserve_bid_timeinterval_start: string | null;
  reserve_bid_timeinterval_end: string | null;
}

const FRRBids: React.FC<FRRBidsProps> = ({
  client,
  setSnackbar,
}: FRRBidsProps) => {
  const [uploadedFile, setUploadedFile] = useState<File>(null);
  const [uploading, setUploading] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({
    uploadedAtFrom: null,
    uploadedAtTo: null,
    processType: null,
    reserve_bid_timeinterval_start: null,
    reserve_bid_timeinterval_end: null,
  });

  const dispatch = useAppDispatch();
  const bids = useAppSelector(selectFRRBids);

  useEffect(() => {
    dispatch(fetchClientFRRBids(client.id, formValues));
  }, [client, dispatch, formValues]);

  const onDrop = (acceptedFiles: any) => {
    setUploadedFile(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    maxFiles: 1,
  });

  const handleDownloadFRRBidTemplate = async () => {
    const link = document.createElement("a");
    link.href = `/files/FRR_bid_template.xlsx`;
    link.download = "FRR_bid_template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSendFile = () => {
    if (uploadedFile) {
      setUploading(true);
      const postData = {
        clientId: client.id,
        bids: uploadedFile,
      };

      uploadFRRBids(postData)
        .then(() => {
          setUploading(false);
          setUploadedFile(null);
          setSnackbar({
            open: true,
            severity: "success",
            message: "FRR bids uploaded successfully",
          });
          dispatch(fetchClientFRRBids(client.id, formValues));
        })
        .catch((error) => {
          setUploading(false);
          console.log({ error });
          setSnackbar({
            open: true,
            severity: "error",
            message: error.response?.data?.message || "Something went wrong",
          });
        });
    }
  };

  const statuses = {
    SENT: {
      color: indigo[400],
      icon: <ArrowOutwardIcon sx={{ width: "16px", height: "16px" }} />,
    },
    ACKNOWLEDGED: {
      color: green[400],
      icon: <CheckIcon sx={{ width: "16px", height: "16px" }} />,
    },
    ERROR: {
      color: red[400],
      icon: <CloseIcon sx={{ width: "16px", height: "16px" }} />,
    },
  };

  const columns = [
    {
      accessorKey: "uploadStatus",
      header: "Upload status",
      cell: () => {
        return (
          <Stack direction="row" alignItems="center">
            <Avatar
              sx={{
                bgcolor:
                  statuses["ACKNOWLEDGED" as keyof typeof statuses].color,
                marginRight: 1,
                width: "24px",
                height: "24px",
              }}
            >
              {statuses["ACKNOWLEDGED" as keyof typeof statuses].icon}
            </Avatar>
            <Typography component="p" sx={{ fontSize: "12px" }}>
              Sent
            </Typography>
          </Stack>
        );
      },
    },
    {
      accessorKey: "processType",
      header: "Process type",
    },
    {
      accessorKey: "senderEic",
      header: "Sender EIC",
    },
    {
      accessorKey: "createdDateTime",
      header: "Created at (EET)",
      cell: (row: any) => {
        return `${dayjs(row.row.original.createdDateTime).format(
          "DD.MM.YYYY HH:mm",
        )}`;
      },
    },
    {
      accessorKey: "reserveBidTimeIntervalStart",
      header: "ReserveBid.TimeInterval.Start (EET)",
      cell: (row: any) => {
        return `${dayjs(row.row.original.reserveBidTimeIntervalStart).format(
          "DD.MM.YYYY HH:mm",
        )}`;
      },
    },
    {
      accessorKey: "reserveBidTimeIntervalEnd",
      header: "ReserveBid.TimeInterval.End (EET)",
      cell: (row: any) => {
        return `${dayjs(row.row.original.reserveBidTimeIntervalEnd).format(
          "DD.MM.YYYY HH:mm",
        )}`;
      },
    },
    {
      accessorKey: "subjectEic",
      header: "Subject EIC",
    },
    {
      accessorKey: "providerEic",
      header: "Provider EIC",
    },
    {
      accessorKey: "divisibleType",
      header: "Divisible type",
    },
    {
      accessorKey: "linkedBidsId",
      header: "Linked bids identification",
    },
    {
      accessorKey: "multipartBidId",
      header: "Multipart bids identification",
    },
    {
      accessorKey: "exclusiveBidId",
      header: "Exclusive bids identification",
    },
    {
      accessorKey: "status",
      header: "Status",
    },
  ];

  const handleSetFormValue = (field: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Box>
      <Grid container columnSpacing={8} sx={{ mt: "1rem" }}>
        <Grid item sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "2rem",
            }}
          >
            <Typography variant="h5" component="h5">
              Frequency Restoration Reserve bids
            </Typography>
          </Box>
          <Box sx={{ mb: "2rem", maxWidth: "1200px" }}>
            <Box sx={{ mt: "2rem" }}>
              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed grey",
                  padding: "20px",
                  cursor: "pointer",
                  backgroundColor: isDragActive ? grey[200] : grey[100],
                  maxWidth: "700px",
                }}
              >
                <input {...getInputProps()} />
                {isDragActive ? (
                  <Typography>Drop the file here</Typography>
                ) : (
                  <Typography component="p">
                    Drag and drop the file here, or click to select from
                    computer
                  </Typography>
                )}
              </Box>

              {uploadedFile && (
                <Box mt={4}>
                  <Typography component="p">Uploaded file:</Typography>
                  <Typography
                    component="p"
                    style={{
                      marginTop: "0.4rem",
                      backgroundColor: grey[100],
                      maxWidth: "max-content",
                      padding: "6px 12px",
                    }}
                  >
                    {uploadedFile.name}
                  </Typography>
                </Box>
              )}

              {uploadedFile && (
                <Box sx={{ display: "flex", mt: 2 }}>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={handleSendFile}
                    loadingIndicator="Uploading..."
                    startIcon={<MultipleStopIcon />}
                    sx={{ mr: 2 }}
                    disabled={!uploadedFile || uploading}
                  >
                    Upload bid(s)
                  </LoadingButton>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item sm={6}>
          <Typography component="p">
            Upload FRR bids for the client by downloading the template and
            filling the required fields.
          </Typography>
          <Typography component="p" sx={{ mt: 1 }}>
            All placed bids and their statuses can be seen in the table.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleDownloadFRRBidTemplate()}
            sx={{ mt: 2 }}
          >
            Download template
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mt: 8 }}>
        <Typography component="p" sx={{ fontSize: "1.2rem" }}>
          FRR bids
        </Typography>
        <Stack direction="row" sx={{ mt: 2 }}>
          <Box display="flex" alignItems="start" mr="0.5rem">
            <Box sx={{ mr: "0.5rem" }}>
              <FormControl
                sx={{
                  minWidth: 196,
                  maxWidth: 196,
                  background: "white",
                }}
                size="small"
              >
                <DatePicker
                  slotProps={{ textField: { size: "small" } }}
                  name="uploadedAtFrom"
                  label="Uploaded at from"
                  value={formValues.uploadedAtFrom}
                  onChange={(value) => {
                    const dateOnly = dayjs(value).startOf("day").toDate();

                    handleSetFormValue(
                      "uploadedAtFrom",
                      dateOnly.toISOString(),
                    );
                  }}
                />
              </FormControl>
            </Box>
            <Box>
              <FormControl
                sx={{
                  minWidth: 196,
                  maxWidth: 196,
                  background: "white",
                }}
                size="small"
              >
                <DatePicker
                  slotProps={{ textField: { size: "small" } }}
                  name="uploadedAtTo"
                  label="Uploaded at to"
                  value={formValues.uploadedAtTo}
                  onChange={(value) => {
                    const dateOnly = dayjs(value).startOf("day").toDate();

                    handleSetFormValue("uploadedAtTo", dateOnly.toISOString());
                  }}
                />
              </FormControl>
            </Box>
          </Box>
          <Box sx={{ mr: 1 }}>
            <Select
              label="Process type"
              id="processType"
              name="processType"
              value={formValues.processType || ""}
              onChange={(event, value) => {
                const formValuesCopy = { ...formValues };

                formValuesCopy.processType = value.props.value;

                setFormValues(formValuesCopy);
              }}
              minWidth={200}
              options={[
                {
                  label: "mFRR",
                  value: "A47",
                },
                {
                  label: "aFRR",
                  value: "A51",
                },
              ]}
            />
          </Box>
          <FormControl
            sx={{
              minWidth: 196,
              maxWidth: 196,
              background: "white",
            }}
            size="small"
          >
            <DateTimePicker
              label="MTU"
              minutesStep={15}
              slotProps={{ textField: { size: "small" } }}
              formatDensity="dense"
              skipDisabled={true}
              value={formValues.reserve_bid_timeinterval_start}
              onChange={(newValue) => {
                const startTime = dayjs(newValue).toDate();
                const endTime = dayjs(newValue).add(15, "minute").toDate();

                const formValuesCopy = { ...formValues };

                formValuesCopy.reserve_bid_timeinterval_start =
                  startTime.toISOString();
                formValuesCopy.reserve_bid_timeinterval_end =
                  endTime.toISOString();

                setFormValues(formValuesCopy);
              }}
            />
          </FormControl>
        </Stack>
        <Box sx={{ mt: 1 }}>
          <Table dense data={bids} isFetching={!bids} columns={columns} />
        </Box>
      </Box>
    </Box>
  );
};

export default FRRBids;
